import type { User } from '../models/api.model';

export interface AccessRule {
  levels?: readonly number[];
  departments?: readonly string[];
  anyOf?: readonly AccessRule[];
}

export const ROLE_LEVEL = {
  ADMIN: 1,
  MANAGER: 2,
  EMPLOYEE: 3,
  EMPLOYEE_ASSISTANT: 4,
} as const;

export const DEPARTMENT = {
  ADMIN: 'ADMIN',
  SALES: 'SALES',
  ACCOUNTING: 'ACC',
  MARKETING: 'MARKETING',
} as const;

const MARKETING_EXPENSE_ACCESS: AccessRule = {
  levels: [ROLE_LEVEL.EMPLOYEE],
  departments: [DEPARTMENT.MARKETING],
};

const STAFF_LEVELS = [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE] as const;
const SALES_DEPARTMENTS = [DEPARTMENT.SALES] as const;
const ACCOUNTING_DEPARTMENTS = [DEPARTMENT.ACCOUNTING] as const;
const BOOKING_VIEW_DEPARTMENTS = [DEPARTMENT.SALES, DEPARTMENT.ACCOUNTING] as const;

export const PAGE_ACCESS = {
  dashboard: {},
  salesDashboard: {
    levels: [...STAFF_LEVELS, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: SALES_DEPARTMENTS,
  },
  accountingDashboard: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  cashFlow: { levels: [ROLE_LEVEL.ADMIN] },
  income: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  accountingExpense: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  customers: {
    levels: [...STAFF_LEVELS, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: SALES_DEPARTMENTS,
  },
  bookings: {
    anyOf: [
      { levels: STAFF_LEVELS, departments: BOOKING_VIEW_DEPARTMENTS },
      { levels: [ROLE_LEVEL.EMPLOYEE_ASSISTANT], departments: SALES_DEPARTMENTS },
    ],
  },
  groupBookings: { levels: STAFF_LEVELS, departments: BOOKING_VIEW_DEPARTMENTS },
  generalExpenses: {
    anyOf: [
      { levels: STAFF_LEVELS, departments: BOOKING_VIEW_DEPARTMENTS },
      MARKETING_EXPENSE_ACCESS,
    ],
  },

  tours: {
    anyOf: [
      {
        levels: [...STAFF_LEVELS, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
        departments: BOOKING_VIEW_DEPARTMENTS,
      },
      { levels: [ROLE_LEVEL.MANAGER], departments: [DEPARTMENT.MARKETING] },
    ],
  },
  expenseRequests: {
    anyOf: [
      { levels: [ROLE_LEVEL.MANAGER], departments: SALES_DEPARTMENTS },
      { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
    ],
  },
  payments: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  expenses: { levels: STAFF_LEVELS, departments: SALES_DEPARTMENTS },
  commissions: { levels: STAFF_LEVELS, departments: SALES_DEPARTMENTS },
  commissionRecoveries: {
    levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: [...SALES_DEPARTMENTS, ...ACCOUNTING_DEPARTMENTS],
  },
  paymentReview: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  expenseReview: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  commissionReview: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  refundReview: { levels: STAFF_LEVELS, departments: ACCOUNTING_DEPARTMENTS },
  expenseApproval: { levels: [ROLE_LEVEL.MANAGER], departments: SALES_DEPARTMENTS },
  commissionApproval: { levels: [ROLE_LEVEL.MANAGER], departments: SALES_DEPARTMENTS },
  refundApproval: { levels: [ROLE_LEVEL.MANAGER], departments: SALES_DEPARTMENTS },
  financialAdjustmentApproval: {
    levels: [ROLE_LEVEL.MANAGER],
    departments: SALES_DEPARTMENTS,
  },
} satisfies Record<string, AccessRule>;

export const BOOKING_ACTION_ACCESS = {
  manage: {
    levels: [...STAFF_LEVELS, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: SALES_DEPARTMENTS,
  },
} satisfies Record<string, AccessRule>;

export const GENERAL_EXPENSE_ACTION_ACCESS = {
  manage: {
    anyOf: [
      {
        levels: [...STAFF_LEVELS, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
        departments: BOOKING_VIEW_DEPARTMENTS,
      },
      MARKETING_EXPENSE_ACCESS,
    ],
  },
} satisfies Record<string, AccessRule>;

export const CUSTOMER_ACTION_ACCESS = {
  create: {
    levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: [DEPARTMENT.SALES],
  },
  edit: {
    levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: [DEPARTMENT.SALES],
  },
  delete: {
    levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE_ASSISTANT],
    departments: [DEPARTMENT.SALES],
  },
} satisfies Record<string, AccessRule>;

export function getHomeRoute(user: User | null): string {
  return user?.role.level === ROLE_LEVEL.EMPLOYEE &&
    user.department.code === DEPARTMENT.MARKETING
    ? '/expenses/general'
    : '/dashboard';
}

export function canAccess(user: User | null, rule: AccessRule = {}): boolean {
  if (!user) return false;

  if (user.role.level === ROLE_LEVEL.ADMIN) return true;
  if (rule.anyOf) return rule.anyOf.some((option) => canAccess(user, option));

  const levelAllowed = !rule.levels || rule.levels.includes(user.role.level);
  const departmentAllowed = !rule.departments || rule.departments.includes(user.department.code);

  return levelAllowed && departmentAllowed;
}
